/** @format */
import { inject, Injectable } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { OtmId, Plan } from 'common-typescript/types';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, distinctUntilChanged, Observable, shareReplay } from 'rxjs';
import { combineLatestWith, map } from 'rxjs/operators';
import { PlanLoaderService } from '../../common/service/plan-loader.service';
import { SelectedPlanStoreService } from '../../common/service/selected-plan-store.service';

const STATE_PARAM_NAME: string = 'planId';

/**
 * This service provides convenient access to the student's "selected plan" for the current view,
 * i.e. the plan defined in the `planId` router parameter. The service does not react to route changes,
 * so it should be provided at (or below) the level in the route hierarchy where the `planId` parameter
 * is defined so that it gets recreated when the selected plan changes. However, `selectedPlan$` will
 * emit the latest version of the plan whenever changes are made to it.
 */
@Injectable()
export class SelectedPlanService {
    private readonly planLoaderService: PlanLoaderService = inject(PlanLoaderService);
    private readonly uiRouterGlobals = inject(UIRouterGlobals);
    private readonly stateService: StateService = inject(StateService);
    private readonly selectedPlanStoreService = inject(SelectedPlanStoreService);

    private readonly selectedPlanId = new BehaviorSubject<OtmId | null>(null);
    private readonly selectedPlanId$: Observable<OtmId | null> = this.selectedPlanId.pipe(
        map(planId => planId ?? this.uiRouterGlobals.params[STATE_PARAM_NAME] ?? this.selectedPlanStoreService.getSelectedPlanId() ?? null),
        distinctUntilChanged(),
    );

    /**
     * Emits the selected plan from {@link PlanLoaderService.allPlans$} that is currently selected,
     * or null if no plan is selected.
     */
    readonly selectedPlan$: Observable<Plan | null> = this.planLoaderService.allPlans$.pipe(
        combineLatestWith(this.selectedPlanId$),
        map(([plans, planId]) => plans.find(plan => plan.id === planId) ?? null),
        distinctUntilChanged(isEqual),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Selects a new plan from among {@link PlanLoaderService.allPlans$}.
     */
    setSelectedPlanId(planId: OtmId): void {
        this.selectedPlanStoreService.setSelectedPlanId(planId);
        this.stateService.go('.', { [STATE_PARAM_NAME]: planId });
        this.selectedPlanId.next(planId);
    }
}
